import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type {
  NativeInstagramStoryItem,
  NativeStoryPlanRow,
  PlannedInstagramItem,
  SocialRevisionPlanRow,
  SocialTargetPlanRow,
} from '@/lib/social-publishing/instagram-profile-plan';
import { mapTargetToPlannedInstagramItem } from '@/lib/social-publishing/instagram-profile-plan';

export interface InstagramFeedPlanSnapshot {
  plannedItems: PlannedInstagramItem[];
  nativeStories: NativeInstagramStoryItem[];
  plannedItemsError: boolean;
  nativeStoriesError: boolean;
}

const TARGET_PAGE_SIZE = 250;
const MAX_TARGET_ROWS = 3000;
const REVISION_BATCH_SIZE = 100;
const PUBLISHED_SYNC_GRACE_MS = 24 * 60 * 60 * 1000;

class FeedPlanningService extends EnhancedBaseService {
  private static instance: FeedPlanningService;

  public static getInstance(): FeedPlanningService {
    if (!FeedPlanningService.instance) {
      FeedPlanningService.instance = new FeedPlanningService({
        deduplication: { enabled: true, ttl: 5000 },
      });
    }
    return FeedPlanningService.instance;
  }

  private async loadInstagramTargets(
    connectionId: string,
    syncGraceStart: string,
  ): Promise<{ rows: SocialTargetPlanRow[]; incomplete: boolean }> {
    const rows: SocialTargetPlanRow[] = [];

    for (let offset = 0; offset < MAX_TARGET_ROWS; offset += TARGET_PAGE_SIZE) {
      const result = await this.client
        .from('social_post_targets')
        .select(
          'id, post_id, revision_id, connection_id, cover_asset_id, profile_plan_rank, placement, native_options_snapshot, scheduled_at, approval_status, delivery_status, native_post_id, updated_at',
        )
        .eq('connection_id', connectionId)
        .eq('platform', 'instagram')
        .eq('readiness_snapshot->>active', 'true')
        .in('placement', ['feed', 'reel', 'story'])
        .or(
          `delivery_status.not.in.(published,published_with_warnings),updated_at.gte.${syncGraceStart}`,
        )
        .order('profile_plan_rank', { ascending: true, nullsFirst: false })
        .order('scheduled_at', { ascending: false, nullsFirst: false })
        .order('id', { ascending: true })
        .range(offset, offset + TARGET_PAGE_SIZE - 1);

      if (result.error) return { rows, incomplete: true };
      const page = (result.data ?? []) as unknown as SocialTargetPlanRow[];
      rows.push(...page);
      if (page.length < TARGET_PAGE_SIZE) return { rows, incomplete: false };
    }

    return { rows, incomplete: true };
  }

  async getInstagramFeedPlan(
    connectionId: string,
  ): Promise<ServiceResponse<InstagramFeedPlanSnapshot>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const syncGraceStart = new Date(Date.now() - PUBLISHED_SYNC_GRACE_MS).toISOString();

      const [targetResult, storyResult] = await Promise.all([
        this.loadInstagramTargets(connectionId, syncGraceStart),
        this.client
          .from('native_story_items')
          .select(
            'connection_id, native_story_id, media_type, thumbnail_path, permalink, published_at, expires_at',
          )
          .eq('connection_id', connectionId)
          .gt('expires_at', new Date().toISOString())
          .order('published_at', { ascending: false }),
      ]);

      let plannedItemsError = targetResult.incomplete;
      const nativeStoriesError = Boolean(storyResult.error);
      const targets = targetResult.rows;
      let revisions: SocialRevisionPlanRow[] = [];
      const coverUrlsById = new Map<string, string>();
      if (targets.length > 0) {
        const revisionIds = [...new Set(targets.map((target) => target.revision_id))];
        const batches: string[][] = [];
        for (let offset = 0; offset < revisionIds.length; offset += REVISION_BATCH_SIZE) {
          batches.push(revisionIds.slice(offset, offset + REVISION_BATCH_SIZE));
        }
        const revisionResults = await Promise.all(
          batches.map((ids) =>
            this.client.from('social_post_revisions').select('id, media_snapshot').in('id', ids),
          ),
        );
        if (revisionResults.some((result) => result.error)) {
          plannedItemsError = true;
        }
        revisions = revisionResults.flatMap((result) =>
          result.error ? [] : ((result.data ?? []) as unknown as SocialRevisionPlanRow[]),
        );

        const coverAssetIds = [
          ...new Set(
            targets.flatMap((target) => (target.cover_asset_id ? [target.cover_asset_id] : [])),
          ),
        ];
        if (coverAssetIds.length > 0) {
          const { data: assets, error: assetError } = await this.client
            .from('social_cover_assets')
            .select('id, public_url, status')
            .in('id', coverAssetIds)
            .eq('status', 'ready');
          if (assetError) plannedItemsError = true;
          for (const asset of assets ?? []) coverUrlsById.set(asset.id, asset.public_url);
        }
      }

      const revisionsById = new Map(revisions.map((revision) => [revision.id, revision]));
      const plannedItems = targets
        .map((target) =>
          mapTargetToPlannedInstagramItem(
            {
              ...target,
              cover_asset_url: target.cover_asset_id
                ? (coverUrlsById.get(target.cover_asset_id) ?? null)
                : null,
            },
            revisionsById.get(target.revision_id),
          ),
        )
        .filter((item): item is PlannedInstagramItem => item !== null);

      const nativeStories = (
        storyResult.error ? [] : ((storyResult.data ?? []) as unknown as NativeStoryPlanRow[])
      ).map((row): NativeInstagramStoryItem => {
        const { data: publicUrl } = this.client.storage
          .from('post_media')
          .getPublicUrl(row.thumbnail_path);
        return {
          id: row.native_story_id,
          connectionId: row.connection_id,
          kind: 'native-story',
          mediaUrl: publicUrl.publicUrl,
          permalink: row.permalink,
          mediaType: row.media_type === 'VIDEO' ? 'VIDEO' : 'IMAGE',
          publishedAt: row.published_at,
          expiresAt: row.expires_at,
        };
      });

      return { plannedItems, nativeStories, plannedItemsError, nativeStoriesError };
    }, 'Failed to load Instagram planning data');
  }
}

export const feedPlanningService = FeedPlanningService.getInstance();
export { FeedPlanningService };
